import React, { useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import { Box, Button, Chip, CircularProgress, Divider, Typography } from '@mui/material'
import PickDateWithoutShiftGrow from '../../components/PickDateWithoutShiftGrow'
import { getFromStorage } from '../../utils/storage'
import axios from 'axios'
import { handleError } from '../../utils/error'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { currentUserActions } from '../../redux/store'
import { DOMAIN } from '../../utils/api'
import { borderFormat, mainGreen } from '../../utils/color'

export default function StockStatisticsByStore() {
	const { storeId } = useParams()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const token = getFromStorage('current-user')?.accessToken
	const [statistics, setStatistics] = useState({})
	const [loadingStatistics, setLoadingStatistics] = useState(true)
	const currentDate = new Date()
	const [firstTimePick, setFirstTimePick] = useState({
		date: currentDate.getDate()?.toString(),
		month: (currentDate.getMonth() + 1)?.toString(),
		year: currentDate.getFullYear()?.toString(),
	})

	const [secondTimePick, setSecondTimePick] = useState({
		date: currentDate.getDate()?.toString(),
		month: (currentDate.getMonth() + 1)?.toString(),
		year: currentDate.getFullYear()?.toString(),
	})

	const setFirstTime = (time) => {
		setFirstTimePick(time)
	}

	const setSecondTime = (time) => {
		setSecondTimePick(time)
	}

	const postStatistics = useCallback(
		async function () {
			try {
				setLoadingStatistics(true)
				const response = await axios({
					url: `${DOMAIN}/admin/sub-stock/statistics/${storeId}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'POST',
					data: { firstTimePick, secondTimePick },
					withCredentials: true,
				})
				setStatistics(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, firstTimePick, secondTimePick, navigate, dispatch, storeId],
	)

	useEffect(() => {
		postStatistics()
	}, [postStatistics])

	return (
		<UserLayout>
			<Box
				sx={{
					maxWidth: 992,
					margin: '0 auto',
					background: '#fff',
					padding: '10px',
				}}>
				<Box display='flex' justifyContent='center' sx={{ mb: 1 }}>
					<Chip sx={{ color: '#fff', background: mainGreen }} label='THỐNG KÊ KHO HÀNG' />
				</Box>
				<Box display='flex' justifyContent='center' alignItems='flex-end'>
					<Typography sx={{ mr: 2, pb: 1 }}>Từ</Typography>
					<PickDateWithoutShiftGrow setTime={setFirstTime} time={firstTimePick} />
				</Box>
				<Box display='flex' justifyContent='center' alignItems='flex-end'>
					<Typography sx={{ mr: 2, pb: 1 }}>đến</Typography>
					<PickDateWithoutShiftGrow setTime={setSecondTime} time={secondTimePick} />
				</Box>
				<Box display='flex' justifyContent='center'>
					<Button
						sx={{
							textTransform: 'none',
							padding: '0 10px',
							mt: 1,
							background: mainGreen,
							'&:hover': { background: mainGreen },
						}}
						variant='contained'
						size='small'
						onClick={() => {
							postStatistics()
						}}>
						Xác nhận
					</Button>
				</Box>
				<Divider sx={{ mt: 2, mb: 1 }} />
				{loadingStatistics && (
					<Box sx={{ textAlign: 'center', mt: 4 }}>
						<CircularProgress sx={{ color: mainGreen }} />
					</Box>
				)}
				{!loadingStatistics && (
					<>
						<Box display='flex'>
							<Box
								sx={{
									width: '48%',
									borderRight: borderFormat,
									marginRight: '15px',
								}}>
								<Typography
									sx={{
										fontSize: '18px',
										fontWeight: 'bold',
										mb: 1,
										mt: 1,
										color: mainGreen,
									}}>
									TỒN ĐẦU KỲ
								</Typography>
								<Typography sx={{ mb: 1, color: '#065E49' }}>
									Nem sống:{' '}
									<b>{statistics?.beginFreshInventory ? `${statistics?.beginFreshInventory} kg` : 'Ko có data'}</b>
								</Typography>
								<Typography sx={{ mb: 1 }}>
									Nem chín:{' '}
									<b> {statistics?.beginCookedInventory ? `${statistics?.beginCookedInventory} kg` : 'Ko có data'}</b>
								</Typography>
							</Box>
							<Box>
								<Typography
									sx={{
										fontSize: '18px',
										fontWeight: 'bold',
										mb: 1,
										mt: 1,
										color: mainGreen,
									}}>
									TỒN CUỐI KỲ
								</Typography>
								<Typography sx={{ mb: 1, color: '#065E49' }}>
									Nem sống:{' '}
									<b>{statistics?.endFreshInventory ? `${statistics?.endFreshInventory} kg` : 'Ko có data'}</b>
								</Typography>
								<Typography sx={{ mb: 1 }}>
									Nem chín:{' '}
									<b>{statistics?.endCookedInventory ? `${statistics?.endCookedInventory} kg` : 'Ko có data'}</b>
								</Typography>
							</Box>
						</Box>
						<Divider sx={{ mt: 1, mb: 1 }} />
						<Typography
							sx={{
								fontSize: '18px',
								fontWeight: 'bold',
								mb: 1,
								mt: 2,
								color: mainGreen,
							}}>
							TỔNG NHẬP SỐNG
						</Typography>
						<Typography sx={{ mb: 1, color: '#065E49' }}>
							Số từ kho tổng: <b>{statistics?.mainStockExport} kg</b>
						</Typography>
						<Typography sx={{ mb: 1, color: '#065E49' }}>
							Số sau khi cân lại: <b>{statistics?.weighedMainStockExport} kg</b>
						</Typography>
						<Divider sx={{ mt: 2, mb: 1 }} />
						<Typography
							sx={{
								fontSize: '18px',
								fontWeight: 'bold',
								mb: 1,
								mt: 2,
								color: mainGreen,
							}}>
							XUẤT NƯỚNG (TỪ KHO SỐNG)
						</Typography>
						<Typography sx={{ mb: 1, color: '#065E49' }}>
							Tổng xuất nướng: <b>{statistics?.grill} kg</b>
						</Typography>
						<Typography sx={{ mb: 1, color: '#065E49' }}>
							Tổng tái nhập sống: <b>{statistics?.reImportFresh} kg</b>
						</Typography>
						<Typography sx={{ mb: 1, color: '#065E49' }}>
							Thực xuất sống: <b>{(statistics?.grill - statistics?.reImportFresh).toFixed(1)} kg</b>
						</Typography>
						<Typography sx={{ mb: 1 }}>
							Thành phẩm: <b>{statistics?.cooked} kg</b>
						</Typography>
						<Divider sx={{ mt: 1, mb: 1 }} />
						<Typography
							sx={{
								fontSize: '18px',
								fontWeight: 'bold',
								mb: 1,
								mt: 2,
								color: mainGreen,
							}}>
							XUẤT CHÍN (BÁN HÀNG)
						</Typography>
						<Typography sx={{ mb: 1 }}>
							Tổng xuất chín: <b>{statistics?.soldHistory} kg</b>
						</Typography>
						<Typography sx={{ mb: 1 }}>
							Tái nhập chín: <b>{statistics?.reImportCooked} kg</b>
						</Typography>
						<Typography sx={{ mb: 1 }}>
							Thực xuất bán: <b>{(statistics?.soldHistory - statistics?.reImportCooked).toFixed(1)} kg</b>
						</Typography>
						<Divider sx={{ mt: 1, mb: 1 }} />
						<Box display='flex'>
							<Box
								sx={{
									width: '48%',
									borderRight: borderFormat,
									marginRight: '15px',
								}}>
								<Typography
									sx={{
										fontSize: '18px',
										fontWeight: 'bold',
										mb: 1,
										mt: 2,
										color: mainGreen,
									}}>
									KIỂM KHO SỐNG
								</Typography>
								<Typography sx={{ mb: 1, color: '#065E49' }}>
									Chênh lệch:{' '}
									{statistics?.differenceCheckFreshStock > 0 ? (
										<b>+{statistics?.differenceCheckFreshStock} kg</b>
									) : (
										<b>{statistics?.differenceCheckFreshStock} kg</b>
									)}
								</Typography>
							</Box>
							<Box>
								<Typography
									sx={{
										fontSize: '18px',
										fontWeight: 'bold',
										mb: 1,
										mt: 2,
										color: mainGreen,
									}}>
									KIỂM KHO CHÍN
								</Typography>
								<Typography sx={{ mb: 1 }}>
									Chênh lệch:{' '}
									{+statistics?.differenceCheckCookedStock > 0 ? (
										<b>+{statistics?.differenceCheckCookedStock} kg</b>
									) : (
										<b>{statistics?.differenceCheckCookedStock} kg</b>
									)}
								</Typography>
							</Box>
						</Box>
					</>
				)}
			</Box>
		</UserLayout>
	)
}
